import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Rich-text post content is stored as HTML. For compact previews (cards, lists)
// where we only want a text snippet, strip the tags so raw markup like "<p>"
// doesn't show. SSR-safe (regex-based, no DOMParser).
export function htmlToPlainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#0?39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

// Convex wraps server-thrown errors with framing like:
//   "[CONVEX M(name)] [Request ID: xxx] Server Error\nUncaught Error: <real message> at fn (path:line:col) ..."
// Returns just the human-facing portion.
export function extractConvexErrorMessage(error: unknown): string {
  // A `ConvexError` carries a structured `data` payload that survives to the
  // client intact. Plain Errors thrown on the server get their message replaced
  // with a generic "Server Error" in production, so prefer `data` when present.
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data: unknown }).data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof (data as { message: unknown }).message === "string"
    ) {
      return (data as { message: string }).message;
    }
  }
  const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  if (!raw) return "";
  const uncaughtIdx = raw.indexOf("Uncaught Error:");
  let msg = uncaughtIdx >= 0 ? raw.slice(uncaughtIdx + "Uncaught Error:".length) : raw;
  const stackMatch = msg.match(/\s+at\s+\S+\s+\(/);
  if (stackMatch?.index !== undefined) {
    msg = msg.slice(0, stackMatch.index);
  }
  return msg.trim() || raw;
}
